import type { SocialProfile } from './SocialProfileModal';

// Local type that's more permissive than SocialProfileModal's strict union
// (Auth0 can return profiles from any provider, not just the 4 allowed ones)
export type ExtractedSocialProfile = {
  platform: string;
  handle: string;
  profile_url: string;
  follower_count?: string;
};

export type { SocialProfile };

// Allowed social platforms for backend user creation
export const ALLOWED_PLATFORMS = new Set(['instagram', 'youtube', 'twitch', 'facebook']);

// Extract social profile data from Auth0 user object
export function extractSocialProfileFromAuth0(auth0User: any): ExtractedSocialProfile[] {
  const socialProfiles: ExtractedSocialProfile[] = [];

  // Parse the 'sub' field to get provider and user_id
  // Format: "provider|user_id" e.g., "facebook|10239019164234205"
  let primaryProvider: string | null = null;
  let primaryUserId: string | null = null;

  if (auth0User.sub) {
    const parts = auth0User.sub.split('|');
    if (parts.length === 2) {
      primaryProvider = parts[0];
      primaryUserId = parts[1];
    }
  }

  // Create social profile from primary provider
  if (primaryProvider && primaryUserId) {
    const profile = buildSocialProfile(primaryProvider, primaryUserId, auth0User);
    if (profile) socialProfiles.push(profile);
  }

  // Also check identities array for additional profiles
  if (auth0User.identities && Array.isArray(auth0User.identities)) {
    for (const identity of auth0User.identities) {
      const platform = identity.provider;
      const userId = identity.user_id;

      // Skip if already added from primary provider
      if (platform === primaryProvider && userId === primaryUserId) continue;

      const profile = buildSocialProfile(platform, userId, auth0User);
      if (profile) socialProfiles.push(profile);
    }
  }

  return socialProfiles;
}

function buildSocialProfile(platform: string, userId: string, auth0User: any): ExtractedSocialProfile | null {
  let handle = '';
  let profileUrl = '';

  switch (platform) {
    case 'google-oauth2':
      handle = auth0User.nickname || auth0User.name;
      profileUrl = `https://accounts.google.com/${userId}`;
      break;
    case 'facebook':
      handle = auth0User.nickname || auth0User.name;
      profileUrl = `https://www.facebook.com/${userId}`;
      break;
    case 'instagram':
      handle = auth0User.nickname || auth0User.username;
      profileUrl = `https://instagram.com/${handle}`;
      break;
    case 'twitter':
      handle = auth0User.screen_name || auth0User.nickname;
      profileUrl = `https://twitter.com/${handle}`;
      break;
    case 'linkedin':
      handle = auth0User.name;
      profileUrl = auth0User.profile || '';
      break;
    case 'github':
      handle = auth0User.nickname || auth0User.login;
      profileUrl = `https://github.com/${handle}`;
      break;
  }

  if (handle && profileUrl) {
    return { platform, handle, profile_url: profileUrl };
  }
  return null;
}

// Filter profiles to only allowed platforms
export function filterAllowedProfiles(profiles: any[]): any[] {
  try {
    return (profiles || []).filter((p: any) => p && ALLOWED_PLATFORMS.has(String(p.platform).toLowerCase()));
  } catch {
    return [];
  }
}

// Detect social profile platform from user input (URL or handle)
export function detectSocialProfile(input: string): SocialProfile | null {
  const normalized = input.trim();
  if (!normalized) return null;

  // If starts with @, assume Instagram
  if (normalized.startsWith('@')) {
    const handle = normalized.slice(1);
    return {
      platform: 'instagram',
      handle,
      profile_url: `https://instagram.com/${handle}`,
    };
  }

  try {
    const url = new URL(normalized.includes('://') ? normalized : `https://${normalized}`);
    const host = url.hostname.toLowerCase();
    const path = url.pathname.replace(/\/$/, '');

    if (host.includes('instagram.com')) {
      const handle = path.split('/').filter(Boolean)[0] || '';
      return handle ? { platform: 'instagram', handle, profile_url: `https://instagram.com/${handle}` } : null;
    }
    if (host.includes('youtube.com')) {
      const parts = path.split('/').filter(Boolean);
      if (parts[0]?.startsWith('@')) {
        const handle = parts[0].slice(1);
        return { platform: 'youtube', handle, profile_url: `https://youtube.com/@${handle}` };
      }
    }
    if (host.includes('twitch.tv')) {
      const handle = path.split('/').filter(Boolean)[0] || '';
      return handle ? { platform: 'twitch', handle, profile_url: `https://twitch.tv/${handle}` } : null;
    }
    if (host.includes('facebook.com')) {
      const handle = path.split('/').filter(Boolean)[0] || '';
      return handle ? { platform: 'facebook', handle, profile_url: `https://facebook.com/${handle}` } : null;
    }
  } catch {
    // Plain text, assume Instagram username
    if (/^[a-zA-Z0-9._]{2,}$/.test(normalized)) {
      return { platform: 'instagram', handle: normalized, profile_url: `https://instagram.com/${normalized}` };
    }
  }

  return null;
}
